import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Pencil, Search } from 'lucide-react';
import styles from './DocumentIntPatientList.module.css';

import { useDebounce } from '../../../services/apiService';
import { useClinic } from '../../../components/ClinicProvider';
import { useGradingHistory } from '../../EmbryoGradingAI/GradingHistoryContext';

import GradingHistory from '../../EmbryoGradingAI/GradingHistory';
import PatientTable from '../../../components/voiceDataEntry/PatientTable';
import Pagination from '../../../components/voiceDataEntry/Pagination';

import type { PatientRow } from './DocumentIntPatientList.types';
import {
  filterPatients,
  getDateRangeForLastMonth,
  loadPatientRows,
  paginatePatients,
} from './DocumentIntPatientList.api';

const DocumentIntPatientList: React.FC = () => {
  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const { isGradingHistoryOpen } = useGradingHistory();

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { startDate, endDate } = useMemo(() => getDateRangeForLastMonth(), []);

  useEffect(() => {
    if (!ClinicId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const rows = await loadPatientRows(
          ClinicId,
          startDate,
          endDate,
          debouncedSearch
        );

        setPatients(rows);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        setError('Failed to load patients');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ClinicId, debouncedSearch, startDate, endDate]);

  const filtered = useMemo(() => {
    return filterPatients(patients, debouncedSearch);
  }, [patients, debouncedSearch]);

  const paged = useMemo(() => {
    return paginatePatients(filtered, page, pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleSelect = (patient: PatientRow) => {
    navigate(`/ai/aiHub/documentintelligence/chat/${patient.id}`, {
      state: {
        from: '/ai/aiHub/documentintelligence',
        patientId: patient.id,
        patientData: patient.raw,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div>
          <div className={styles.titleLine}>
            <h1 className={styles.pageTitle}>Document Intelligence</h1>

            <div className={styles.iconActions}>
              <button className={styles.iconButton} aria-label="Edit">
                <Pencil size={16} />
              </button>
              <button className={styles.iconButton} aria-label="Open">
                <ExternalLink size={16} />
              </button>
            </div>
          </div>

          <div className={styles.sectionSubtitle}>
            Select patients to proceed:
          </div>
        </div>

        <div className={styles.searchBox}>
          <Search className={styles.searchIcon} size={18} />
          <input
            type="text"
            placeholder="Search & Select Patient by Name"
            className={styles.searchInput}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <PatientTable
        rows={paged}
        loading={loading}
        error={error}
        onSelect={handleSelect}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={filtered.length}
        onPageChange={setPage}
      />

      {isGradingHistoryOpen && (
        <div
          style={{
            position: 'fixed',
            top: '152px',
            left: '293px',
            width: 360,
            height: 'calc(100vh - 152px)',
            zIndex: 1000,
            background: 'transparent',
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <GradingHistory />
        </div>
      )}
    </div>
  );
};

export default DocumentIntPatientList;